import { FileBarChart2, FileText, Users, Wallet, Package2, TrendingUp, Download, Calendar } from "lucide-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { BarChart, Bar, ResponsiveContainer, XAxis, YAxis, CartesianGrid, Tooltip, Cell } from "recharts";
import { Inbox } from "lucide-react";
import { AdminAPI } from "@/lib/api";
import { downloadCsv } from "@/lib/download";
import { useEffect, useState } from "react";
import { toast } from "sonner";

const fmtDate = (d) => (d ? new Date(d).toLocaleString("id-ID") : "");

const MONTHS_ID = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agu", "Sep", "Okt", "Nov", "Des"];
const ymLabel = (ym) => MONTHS_ID[(Number(String(ym).split("-")[1]) - 1 + 12) % 12] || ym;

const ChartEmpty = ({ note }) => (
  <div className="h-60 grid place-items-center text-center">
    <div><Inbox className="mx-auto text-ink/30" size={28} /><p className="text-sm text-ink/55 mt-2">{note}</p></div>
  </div>
);

const reports = [
  { key: "keuangan", icon: Wallet, title: "Laporan Keuangan", desc: "Buku besar transaksi (ledger)", format: "CSV", color: "bg-forest/10 text-forest" },
  { key: "keanggotaan", icon: Users, title: "Laporan Keanggotaan", desc: "Daftar anggota & simpanan", format: "CSV", color: "bg-amber-warm/15 text-amber-700" },
  { key: "shu", icon: TrendingUp, title: "Laporan SHU", desc: "SHU per anggota per tahun", format: "CSV", color: "bg-sage/20 text-forest" },
  { key: "stok", icon: Package2, title: "Laporan Stok & Harga", desc: "Katalog, harga, & stok produk", format: "CSV", color: "bg-terracotta/10 text-terracotta" },
  { key: "audit", icon: FileText, title: "Audit Trail", desc: "Log aktivitas seluruh admin", format: "CSV", color: "bg-stone-soft/40 text-ink/75" },
  { key: "operasional", icon: FileBarChart2, title: "Laporan Operasional", desc: "GMV & pertumbuhan bulanan", format: "CSV", color: "bg-forest/10 text-forest" },
];

// Ambil data nyata sesuai jenis laporan lalu unduh sebagai CSV.
async function buildReport(key) {
  switch (key) {
    case "keuangan": {
      const rows = await AdminAPI.transactions();
      return { name: "laporan-keuangan", headers: ["Kode", "Tanggal", "Tipe", "Dompet", "Arah", "Nominal", "Referensi"],
        rows: (rows || []).map((t) => [t.trx_code, fmtDate(t.created_at), t.type, t.wallet_type, t.direction, t.amount, t.reference || ""]) };
    }
    case "keanggotaan": {
      const rows = await AdminAPI.members();
      return { name: "laporan-keanggotaan", headers: ["No. Anggota", "Nama", "Email", "Telepon", "Role", "Cabang", "Bergabung"],
        rows: (rows || []).map((m) => [m.member_number || m.id, m.name, m.email || "", m.phone_masked || "", m.role, m.branch || "", fmtDate(m.joined)]) };
    }
    case "shu": {
      const rows = await AdminAPI.shuReport();
      return { name: "laporan-shu", headers: ["No. Anggota", "Nama", "Tahun", "SHU", "Jasa Modal", "Jasa Usaha"],
        rows: (rows || []).map((s) => [s.member_number || "", s.name, s.year, s.amount, s.jasa_modal, s.jasa_usaha]) };
    }
    case "stok": {
      const rows = await AdminAPI.stockReport();
      return { name: "laporan-stok", headers: ["SKU", "Produk", "Kategori", "Sumber", "HPP", "Harga Publik", "Harga Anggota", "Stok", "Status"],
        rows: (rows || []).map((p) => [p.sku, p.product_name, p.category, p.source_type, p.price_base_cost, p.price_non_member, p.price_member, p.stock_quantity, p.status]) };
    }
    case "audit": {
      const rows = await AdminAPI.auditLogs();
      return { name: "audit-trail", headers: ["Waktu", "Pelaku", "Peran", "Aksi", "IP"],
        rows: (rows || []).map((l) => [fmtDate(l.created_at), l.actor_name || "Sistem", l.actor_role || "", l.action_type, l.ip_address || ""]) };
    }
    case "operasional": {
      const r = await AdminAPI.reports();
      let cum = 0;
      const rows = (r.monthly || []).map((m) => [m.ym, m.orders, m.gmv]);
      const growth = (r.member_growth || []).map((m) => { cum += Number(m.baru); return [m.ym, m.baru, cum]; });
      return { name: "laporan-operasional", headers: ["Bulan", "Jumlah Pesanan", "GMV"],
        rows: [...rows, [], ["Bulan", "Anggota Baru", "Kumulatif"], ...growth] };
    }
    default: return null;
  }
}

export default function AdminReports() {
  const [salesData, setSalesData] = useState([]);
  const [growthData, setGrowthData] = useState([]);
  const [loaded, setLoaded] = useState(false);

  // Agregasi nyata: GMV bulanan (dalam ribu Rp) & pertumbuhan anggota kumulatif.
  useEffect(() => {
    AdminAPI.reports().then((r) => {
      setSalesData((r.monthly || []).map((m) => ({ month: ymLabel(m.ym), penjualan: Math.round(Number(m.gmv) / 1000) })));
      let cum = 0;
      setGrowthData((r.member_growth || []).map((m) => { cum += Number(m.baru); return { month: ymLabel(m.ym), anggota: cum }; }));
    }).catch(() => {}).finally(() => setLoaded(true));
  }, []);

  const hasSales = salesData.some((d) => d.penjualan > 0);
  const hasGrowth = growthData.length > 0;
  const [downloading, setDownloading] = useState("");

  const handleDownload = async (key, title) => {
    setDownloading(key);
    try {
      const built = await buildReport(key);
      if (!built || built.rows.length === 0) return toast.info(`${title}: belum ada data`);
      downloadCsv(built.name, built.headers, built.rows);
      toast.success(`${title} diunduh (${built.rows.length} baris)`);
    } catch (err) {
      toast.error(err?.response?.data?.error || `Gagal membuat ${title}`);
    } finally { setDownloading(""); }
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl lg:text-4xl font-bold text-ink tracking-tight">Laporan & Analitik</h1>
          <p className="text-ink/75 mt-2 text-sm">Generate laporan keuangan, anggota, SHU, dan audit trail secara otomatis.</p>
        </div>
        <div className="flex gap-2">
          <Button className="rounded-lg bg-forest hover:bg-forest-dark text-white" onClick={() => handleDownload("operasional", "Laporan Operasional")} data-testid="reports-custom"><FileText size={15} /> Unduh Ringkasan</Button>
        </div>
      </div>

      {/* Visualisasi */}
      <div className="grid lg:grid-cols-2 gap-5">
        <Card className="rounded-xl border-stone-soft p-6">
          <h3 className="font-display font-semibold text-ink">Penjualan 6 Bulan</h3>
          <p className="text-xs text-ink/55">dalam ribu Rupiah</p>
          {!loaded ? <ChartEmpty note="Memuat…" /> : !hasSales ? <ChartEmpty note="Belum ada penjualan" /> : (
          <div className="h-60 mt-4">
            <ResponsiveContainer>
              <BarChart data={salesData}>
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" vertical={false} />
                <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                <YAxis axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                <Tooltip />
                <Bar dataKey="penjualan" radius={[8, 8, 0, 0]}>
                  {salesData.map((_, i) => <Cell key={i} fill={i === salesData.length - 1 ? "#D48C45" : "#2D5A27"} />)}
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
          )}
        </Card>

        <Card className="rounded-xl border-stone-soft p-6">
          <h3 className="font-display font-semibold text-ink">Pertumbuhan Anggota</h3>
          {!loaded ? <ChartEmpty note="Memuat…" /> : !hasGrowth ? <ChartEmpty note="Belum ada anggota terdaftar" /> : (
          <div className="h-60 mt-4">
            <ResponsiveContainer>
              <BarChart data={growthData} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" stroke="#E6E2D8" horizontal={false} />
                <XAxis type="number" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                <YAxis type="category" dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 11, fill: "#889285" }} />
                <Tooltip />
                <Bar dataKey="anggota" radius={[0, 8, 8, 0]} fill="#85A37E" />
              </BarChart>
            </ResponsiveContainer>
          </div>
          )}
        </Card>
      </div>

      <div>
        <h3 className="font-display font-semibold text-ink text-lg mb-4">Laporan Tersedia</h3>
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {reports.map((r) => (
            <Card key={r.key} className="rounded-xl border-stone-soft p-6 hover-lift" data-testid={`report-${r.key}`}>
              <div className={`w-11 h-11 rounded-xl grid place-items-center ${r.color}`}><r.icon size={20} /></div>
              <h4 className="font-display font-semibold text-ink mt-4">{r.title}</h4>
              <p className="text-sm text-ink/75 mt-1">{r.desc}</p>
              <div className="flex items-center justify-between mt-5 pt-4 border-t border-stone-soft/60">
                <Badge className="bg-cream text-ink/75 hover:bg-cream border-0 rounded-full text-[10px]">{r.format}</Badge>
                <Button size="sm" disabled={downloading === r.key} onClick={() => handleDownload(r.key, r.title)} className="rounded-lg bg-forest hover:bg-forest-dark text-white" data-testid={`report-download-${r.key}`}>
                  <Download size={13} /> {downloading === r.key ? "Menyiapkan…" : "Unduh CSV"}
                </Button>
              </div>
            </Card>
          ))}
        </div>
      </div>
    </div>
  );
}
